import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sprout, Trophy, Loader2, Sparkles, Lightbulb, UsersRound } from "lucide-react";
import { api, fmt, fmtDelta } from "../../lib/game";
import { Button } from "../ui/button";
import { MembersLeaderboard } from "./PlayTogether";

export const HouseholdShareView = ({ code, onPlay }) => {
  const [hh, setHh] = useState(null);
  const [recap, setRecap] = useState(null);

  useEffect(() => {
    api
      .get(`/households/code/${code}`)
      .then(({ data }) => {
        setHh(data);
        if (data.status === "complete") {
          api.post(`/households/${data.id}/recap`).then(({ data }) => setRecap(data)).catch(() => setRecap(false));
        }
      })
      .catch(() => setHh(false));
  }, [code]);

  if (hh === null)
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 text-pink-400 animate-spin" /></div>;
  if (hh === false)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-4 text-center">
        <p className="text-slate-400">This shared household result could not be found.</p>
        <Button onClick={onPlay} className="bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold">Play Burgeon</Button>
      </div>
    );

  const change = hh.net_worth - (hh.starting_net_worth ?? 0);

  return (
    <div className="bg-grid min-h-screen px-4 py-10" data-testid="household-share-view">
      <div className="max-w-2xl mx-auto space-y-5">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Sprout className="w-5 h-5 text-slate-950" strokeWidth={2.4} />
          </div>
          <span className="font-heading text-xl font-bold text-slate-100">Burgeon</span>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="rounded-3xl border border-pink-500/30 bg-slate-900/70 p-6 sm:p-8 text-center"
        >
          <div className="w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-pink-500 to-rose-600 flex items-center justify-center mb-4">
            {hh.status === "complete" ? <Trophy className="w-7 h-7 text-slate-950" /> : <UsersRound className="w-7 h-7 text-slate-950" />}
          </div>
          <p className="text-xs font-semibold uppercase tracking-widest text-pink-400">Household Result</p>
          <h1 className="font-heading text-3xl font-bold text-slate-50 mt-2">{hh.name}</h1>
          <p className="text-slate-400 text-sm mt-1">{hh.members.map((m) => m.nickname).join(" & ")}</p>
          <div className="mt-5">
            <p className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
              {hh.status === "complete" ? "Final Net Worth" : "Net Worth so far"}
            </p>
            <p className="font-mono text-4xl font-bold text-slate-50 mt-1">{fmt(hh.net_worth)}</p>
            <p className={`text-sm font-mono font-semibold mt-1 ${change >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {hh.status === "complete" ? `${fmtDelta(change)} across ${hh.total_rounds} rounds` : `Round ${hh.current_round} of ${hh.total_rounds}`}
            </p>
          </div>
        </motion.div>

        {recap && recap !== false && (
          <div className="rounded-3xl border border-pink-500/30 bg-slate-900/70 p-6">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="w-4 h-4 text-pink-300" />
              <span className="text-xs font-semibold uppercase tracking-widest text-pink-300">Household Coach Recap</span>
            </div>
            <p className="text-slate-200 leading-relaxed text-[15px]">{recap.text}</p>
            {recap.tip && (
              <div className="mt-4 rounded-2xl border border-yellow-500/30 bg-yellow-500/[0.07] p-3.5 flex gap-2.5">
                <Lightbulb className="w-5 h-5 text-yellow-400 shrink-0 mt-0.5" />
                <p className="text-sm text-slate-200">{recap.tip}</p>
              </div>
            )}
          </div>
        )}

        {hh.decisions?.length > 0 && <MembersLeaderboard decisions={hh.decisions} members={hh.members} />}

        <div className="text-center">
          <Button
            data-testid="household-share-play-btn"
            onClick={onPlay}
            className="h-12 px-8 bg-gradient-to-r from-emerald-500 to-teal-600 text-slate-950 font-bold rounded-xl"
          >
            Play your own game
          </Button>
        </div>
      </div>
    </div>
  );
};
